import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Entity, RELATION_DEPENDENCY_OF } from '@backstage/catalog-model';
import { CopyTextButton, InfoCard, Link } from '@backstage/core-components';
import {
  useEntity,
  useEntityRefLink,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';

interface RecordValue {
  text: string;
  /** The record entity this value came from, so it can be linked to. */
  entity?: Entity;
}

function CodeBlock({ text, entity }: RecordValue) {
  const entityRefLink = useEntityRefLink();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        my: 1,
        backgroundColor: 'rgba(0,0,0,0.04)',
        borderRadius: 1,
        p: 1,
      }}
    >
      <Typography
        component="pre"
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          flex: 1,
          m: 0,
        }}
      >
        {text}
      </Typography>
      {entity && (
        <IconButton
          size="small"
          component={Link}
          to={entityRefLink(entity)}
          title="View record"
          aria-label="View record"
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      )}
      <CopyTextButton text={text} />
    </Box>
  );
}

function normalizeFqdn(name: string | undefined): string | undefined {
  return name?.replace(/\.$/, '').toLowerCase();
}

/**
 * rrdata values (paired with their source entity) from every related record
 * of the given DNS record type, owned by the zone itself.
 *
 * A zone's related records include NS (and other) RRsets that delegate its
 * *subdomains* to other zones/providers — those still legitimately depend on
 * this zone, but aren't the zone's own apex records. When ownerFqdn is given,
 * only records whose own name matches it are included, so a delegation NS
 * record for e.g. "foo.example.com" doesn't get shown as if it were
 * "example.com"'s own name servers.
 */
function recordValuesFor(
  records: Entity[],
  recordType: string,
  ownerFqdn?: string,
): RecordValue[] {
  return records
    .filter(
      r => r.metadata.annotations?.['gcp.com/dns-record-type'] === recordType,
    )
    .filter(r => {
      if (!ownerFqdn) return true;
      const details = r.spec?.gcpDetails as { name?: string } | undefined;
      return normalizeFqdn(details?.name) === ownerFqdn;
    })
    .flatMap(entity => {
      const details = entity.spec?.gcpDetails as
        { rrdata?: string[] } | undefined;
      return (details?.rrdata ?? []).map(text => ({ text, entity }));
    });
}

export function DnsZoneCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};
  const { entities: relatedRecords } = useRelatedEntities(entity, {
    type: RELATION_DEPENDENCY_OF,
  });

  if (annotations['gcp.com/service'] !== 'cloud-dns-zone') {
    return null;
  }

  const details = (entity.spec?.gcpDetails ?? undefined) as
    Record<string, unknown> | undefined;

  const { dnsName, visibility, nameServers, description, dnssecState } =
    (details ?? {}) as {
      dnsName?: string;
      visibility?: string;
      nameServers?: string[];
      description?: string;
      dnssecState?: string;
    };

  const excludedNote = annotations['gcp.com/dns-records-excluded-note'];
  const zoneApex = normalizeFqdn(dnsName);
  const soaValues = recordValuesFor(relatedRecords ?? [], 'SOA', zoneApex);
  const nsValues = recordValuesFor(relatedRecords ?? [], 'NS', zoneApex);
  // Prefer the linked NS record (the actual catalog entity, linkable) over
  // the zone's own cached nameServers field, but fall back to it if the NS
  // record type is excluded from ingestion (e.g. via dns.recordTypeDenyList)
  // — the zone resource always carries this regardless of record filtering.
  const displayedNameServers: RecordValue[] =
    nsValues.length > 0
      ? nsValues
      : (nameServers ?? []).map(text => ({ text }));

  return (
    <InfoCard title="Cloud DNS Zone">
      <Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {visibility && (
            <Chip label={visibility} size="small" variant="outlined" />
          )}
          {dnssecState && (
            <Chip
              label={`DNSSEC: ${dnssecState}`}
              size="small"
              color={dnssecState === 'on' ? 'success' : 'warning'}
              variant="outlined"
            />
          )}
        </Box>

        {dnsName && (
          <>
            <Typography variant="subtitle2">DNS Name</Typography>
            <CodeBlock text={dnsName} />
          </>
        )}

        {displayedNameServers.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Name Servers
            </Typography>
            {displayedNameServers.map((ns, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <CodeBlock
                key={`${ns.text}-${i}`}
                text={ns.text}
                entity={ns.entity}
              />
            ))}
          </>
        )}

        {soaValues.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              SOA Record
            </Typography>
            {soaValues.map((soa, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <CodeBlock
                key={`${soa.text}-${i}`}
                text={soa.text}
                entity={soa.entity}
              />
            ))}
          </>
        )}

        {description && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {description}
          </Typography>
        )}

        {excludedNote && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: 'block', mt: 2 }}
          >
            {excludedNote}
          </Typography>
        )}
      </Box>
    </InfoCard>
  );
}
